import { FC, useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { RebillInvoiceDetails, RebillInvoiceDetailsSchema } from './schemas';
import { GraniteInput } from 'components/V2/Input/GraniteInput';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import FormSection from 'components/FormSection';
import Divider from 'components/Divider/Divider';
import { Add, Trash } from 'react-ionicons';
import AddressInput from 'screens/InvoiceAnalyzer/AddressInput';
import { SiteAddress } from 'api/addresssearch/schema';
import { GraniteSelect, OptionType } from 'components/Select/Select';
import PhoneNumberInput from 'components/V2/Input/PhoneNumber';
import Loader from 'components/Loader';

type UnitOption = { label: 'Mb/s' | 'Gb/s'; value: 'Mbps' | 'Gbps' };
const unitOptions = [
  { label: 'Mb/s', value: 'Mbps' },
  { label: 'Gb/s', value: 'Gbps' },
] as const satisfies readonly UnitOption[];

interface InvoiceDetailsFormProps {
  onBack: (draft: RebillInvoiceDetails) => void;
  onSubmit: (data: RebillInvoiceDetails) => void;
  isLoading: boolean;
  initialData?: RebillInvoiceDetails;
  cwLocations?:
    | (SiteAddress & { account_name: string; site_name: string })[]
    | null;
  location: {
    address1: string;
    address2?: string | null;
    city: string;
    state: string;
    zip_code: string;
    id?: string;
  } | null;
  selectedOption: OptionType | null;
  setSelectedOption: React.Dispatch<React.SetStateAction<OptionType | null>>;
}

export const INVOICE_SERVICE_TYPES = [
  'Broadband',
  'DIA',
  'PIP/GSE',
  'Mobility',
  'Hosted PBX/EPOTS',
  'DID',
  'POTS',
  'RCF',
  'SIP',
  'Toll-Free',
  'LD',
  'Television',
];

export const INVOICE_SERVICE_SUBTYPES = [
  'ADSL',
  'Cable',
  'Dry Loop',
  'Fiber',
  'Satellite',
  'T1',
  'Wireless',
];

const EMPTY_ROW = {
  service_type: { label: '', value: '' },
  service_identifier: '',
  wtns: [] as string[],
};

const InvoiceDetailsForm: FC<InvoiceDetailsFormProps> = ({
  onBack,
  onSubmit,
  isLoading,
  initialData,
  cwLocations,
  location,
  selectedOption,
  setSelectedOption,
}) => {
  const [wtnCounts, setWtnCounts] = useState<{ [key: number]: number }>({});

  const defaults = useMemo<RebillInvoiceDetails>(() => {
    if (initialData) return initialData;
    return {
      vendor_name: '',
      vendor_account_number: '',
      mrc: '' as unknown as number,
      contract_start_date: '',
      contract_end_date: '',
      services: [EMPTY_ROW],
    };
  }, [initialData]);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    getValues,
    reset,
  } = useForm<RebillInvoiceDetails>({
    resolver: zodResolver(RebillInvoiceDetailsSchema),
    defaultValues: defaults,
  });

  useEffect(() => {
    reset(defaults);
  }, [defaults, reset]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'services',
  });

  const renderBandwidth = (index: number) => (
    <div className="flex flex-row items-center gap-0">
      <GraniteInput
        {...register(`services.${index}.bandwidth.amount`)}
        label="Bandwidth"
        error={
          errors.services?.[index]?.bandwidth?.amount?.message ||
          errors.services?.[index]?.bandwidth?.unit?.message
        }
        innerInputClassName="!w-full !box-border !rounded-r-none"
      />
      <Controller
        name={`services.${index}.bandwidth.unit`}
        control={control}
        render={({ field }) => (
          <GraniteSelect<UnitOption>
            {...field}
            options={unitOptions}
            label="unit"
            value={field.value}
            onChange={field.onChange}
            className="basis-1/2 self-baseline [&>label]:invisible"
            classNames={{ control: () => '!rounded-l-none' }}
          />
        )}
      />
    </div>
  );

  const addService = () => {
    if (fields.length < 5) {
      append({
        service_type: { label: '', value: '' },
        service_identifier: '',
        wtns: [],
      });
      setWtnCounts((prev) => ({
        ...prev,
        [fields.length]: 0,
      }));
    }
  };

  const removeService = (index: number) => {
    remove(index);
    setWtnCounts((prev) => {
      const updated = { ...prev };
      delete updated[index];
      return updated;
    });
  };

  const handleBack = () => {
    onBack(getValues());
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex h-full w-full flex-col gap-12 p-8"
    >
      <AddressInput
        locations={cwLocations ?? []}
        location={location}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        onCreateNewAccount={() => {}}
        showDropdownCaret={false}
      />
      <FormSection gridClassName="grid-cols-3" title="Invoice details">
        <GraniteInput
          {...register(`vendor_name`)}
          label="Vendor name"
          error={errors.vendor_name?.message}
        />
        <GraniteInput
          {...register(`vendor_account_number`)}
          label="Vendor account #"
          error={errors.vendor_account_number?.message}
        />
        <GraniteInput
          {...register(`mrc`)}
          label="Total MRC"
          error={errors.mrc?.message}
        />
        <GraniteInput
          {...register(`contract_start_date`)}
          label="Contract start date (optional)"
          placeholder="mm-dd-yyyy"
          error={errors.contract_start_date?.message}
        />
        <GraniteInput
          {...register(`contract_end_date`)}
          label="Contract end date (optional)"
          placeholder="mm-dd-yyyy"
          error={errors.contract_end_date?.message}
        />
      </FormSection>
      <Divider layout="horizontal" variant="subdued" />
      <FormSection
        title="Services"
        className="w-full"
        gridClassName="grid-cols-1"
      >
        {fields.map((field, index) => {
          const serviceType =
            watch(`services.${index}.service_type`)?.value || '';

          return (
            <div
              key={field.id}
              className="relative mb-6 grid auto-rows-auto grid-cols-[40px,1fr,20px] gap-4"
            >
              {/* Row number */}
              <div className="col-start-1 flex items-center justify-center pt-6">
                <span className="text-sm font-medium text-content-base-subdued">
                  {index + 1}
                </span>
              </div>

              <div className="col-start-2 flex flex-col gap-4">
                {[
                  'DID',
                  'POTS',
                  'RCF',
                  'SIP',
                  'Toll-Free',
                  'LD',
                  'Hosted PBX/EPOTS',
                ].includes(serviceType) ? (
                  <>
                    <div className="grid grid-cols-[1fr,1fr,1fr] gap-4">
                      <Controller
                        name={`services.${index}.service_type`}
                        control={control}
                        render={({ field }) => (
                          <GraniteSelect
                            {...field}
                            options={INVOICE_SERVICE_TYPES.map((t) => ({
                              label: t,
                              value: t,
                            }))}
                            label="Service type"
                            onChange={(option) => field.onChange(option)}
                            value={field.value}
                            error={
                              errors.services?.[index]?.service_type?.value
                                ?.message
                            }
                          />
                        )}
                      />

                      <PhoneNumberInput
                        name={`services.${index}.btn`}
                        label="Service identifier"
                        error={errors.services?.[index]?.btn?.message}
                        control={control}
                        innerInputClassName="w-full"
                      />
                      <PhoneNumberInput
                        name={`services.${index}.wtns.0`}
                        label="Working telephone number"
                        error={errors.services?.[index]?.wtns?.[0]?.message}
                        control={control}
                      />
                    </div>
                    {(wtnCounts[index] || 1) > 1 && (
                      <div className="grid grid-cols-[1fr,20px,1fr,20px,1fr,20px] gap-4">
                        {Array.from(
                          { length: wtnCounts[index] - 1 },
                          (_, i) => (
                            <>
                              <PhoneNumberInput
                                name={`services.${index}.wtns.${i + 1}`}
                                label={`Working telephone number`}
                                error={
                                  errors.services?.[index]?.wtns?.[i + 1]
                                    ?.message
                                }
                                className="col-span-1"
                                control={control}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setWtnCounts((prev) => ({
                                    ...prev,
                                    [index]: (prev[index] || 1) - 1,
                                  }));
                                  const existing = watch(
                                    `services.${index}.wtns`,
                                  ) || [''];
                                  setValue(`services.${index}.wtns`, [
                                    ...existing.slice(0, i + 1),
                                    ...existing.slice(i + 2),
                                  ]);
                                }}
                                aria-label={`Remove WTN ${i + 2}`}
                                className="flex items-center justify-center pt-7"
                              >
                                <Trash
                                  width="18px"
                                  height="18px"
                                  color="#FF315E"
                                />
                              </button>
                            </>
                          ),
                        )}
                      </div>
                    )}
                    {(wtnCounts[index] || 1) < 5 && (
                      <GraniteButton
                        type="button"
                        variant="secondary"
                        className="mt-2 w-fit"
                        onClick={() => {
                          setWtnCounts((prev) => ({
                            ...prev,
                            [index]: (prev[index] || 1) + 1,
                          }));
                          const existing = watch(`services.${index}.wtns`) || [
                            '',
                          ];
                          setValue(`services.${index}.wtns`, [...existing, '']);
                        }}
                      >
                        Add additional WTN
                      </GraniteButton>
                    )}
                  </>
                ) : (
                  <div className="grid auto-cols-fr grid-flow-col items-start gap-4">
                    <Controller
                      name={`services.${index}.service_type`}
                      control={control}
                      render={({ field }) => (
                        <GraniteSelect
                          {...field}
                          options={INVOICE_SERVICE_TYPES.map((t) => ({
                            label: t,
                            value: t,
                          }))}
                          label="Service type"
                          onChange={(option) => field.onChange(option)}
                          value={field.value}
                          error={
                            errors.services?.[index]?.service_type?.value
                              ?.message
                          }
                        />
                      )}
                    />
                    {![
                      'DID',
                      'POTS',
                      'RCF',
                      'SIP',
                      'Toll-Free',
                      'LD',
                      'Hosted PBX/EPOTS',
                    ].includes(serviceType) && (
                      <GraniteInput
                        {...register(`services.${index}.service_identifier`)}
                        label="Service identifier"
                        error={
                          errors.services?.[index]?.service_identifier?.message
                        }
                        innerInputClassName="w-full"
                      />
                    )}
                    {serviceType === 'Mobility' && (
                      <div>{renderBandwidth(index)}</div>
                    )}
                    {['Broadband', 'DIA', 'PIP/GSE'].includes(serviceType) && (
                      <>
                        <GraniteSelect
                          {...register(`services.${index}.subtype`)}
                          options={INVOICE_SERVICE_SUBTYPES.map((s) => ({
                            label: s,
                            value: s,
                          }))}
                          label="Data service type"
                          onChange={(option) =>
                            option &&
                            setValue(`services.${index}.subtype`, option.value)
                          }
                          error={errors.services?.[index]?.subtype?.message}
                          className="w-full"
                        />
                        {renderBandwidth(index)}
                      </>
                    )}
                  </div>
                )}
              </div>

              {index > 0 && (
                <button
                  type="button"
                  onClick={() => removeService(index)}
                  aria-label="Remove service"
                  className="col-start-3 w-2 justify-self-center pt-7"
                >
                  <Trash width="22px" height="22px" color="#FF315E" />
                </button>
              )}
            </div>
          );
        })}

        <GraniteButton
          type="button"
          variant="secondary"
          className="mt-2 w-fit"
          onClick={addService}
          disabled={fields.length >= 5}
        >
          Add additional services <Add width="20px" height="20px" />
        </GraniteButton>
      </FormSection>

      <div className="mt-4 flex gap-5">
        <GraniteButton
          size="large"
          onClick={handleBack}
          variant="secondary"
          type="button"
        >
          Back
        </GraniteButton>
        <GraniteButton
          size="large"
          variant="primary"
          type="submit"
          disabled={isLoading}
        >
          Consolidate invoice
          {isLoading && <Loader animationClassname="!w-5 !h-5" />}
        </GraniteButton>
      </div>
    </form>
  );
};

export default InvoiceDetailsForm;
